export default function HowItWorks() {
  const steps = [
    {
      number: '01',
      icon: 'ri-phone-fill',
      title: 'Call Us 24/7',
      description: 'Contact Indoor-Restore Services any time — day or night — at (209) 451-2288. Our emergency dispatch team is always standing by to take your call and gather the details needed to send the right crew and equipment to your Merced County property immediately.'
    },
    {
      number: '02',
      icon: 'ri-search-eye-line',
      title: 'Free On-Site Assessment',
      description: 'A certified IICRC technician arrives at your property — typically within 60 minutes — and performs a thorough inspection using moisture meters, thermal imaging cameras, and hygrometers. We identify all affected areas, including hidden moisture inside walls, subfloors, and structural cavities.'
    },
    {
      number: '03',
      icon: 'ri-drop-line',
      title: 'Water Extraction & Drying',
      description: 'We deploy industrial-grade truck-mounted extractors to remove standing water fast, then set up high-velocity air movers and LGR dehumidifiers to begin structural drying. Our equipment runs continuously, monitored daily, until your property reaches safe moisture levels per IICRC S500 standards.'
    },
    {
      number: '04',
      icon: 'ri-virus-line',
      title: 'Mold Prevention & Remediation',
      description: 'In Merced\'s warm Central Valley climate, mold can begin colonizing within 24–48 hours of water intrusion. We apply EPA-registered antimicrobial treatments to all affected surfaces, contain any active mold growth, and perform full remediation to protect your family\'s health and your property\'s structural integrity.'
    },
    {
      number: '05',
      icon: 'ri-file-list-3-line',
      title: 'Insurance Documentation',
      description: 'We prepare a complete, photo-documented damage report and communicate directly with your insurance adjuster throughout the entire process. Our team has extensive experience working with all major carriers — State Farm, Allstate, Farmers, AAA, and more — to ensure your claim is processed accurately and efficiently.'
    },
    {
      number: '06',
      icon: 'ri-home-smile-line',
      title: 'Full Restoration & Rebuild',
      description: 'Once drying is complete and clearance testing confirms safe moisture levels, our reconstruction team restores your property to its pre-damage condition — or better. From drywall and flooring to painting and finish carpentry, we handle every phase of the rebuild so you only need one contractor.'
    }
  ];

  return (
    <section id="how-it-works" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block bg-teal-50 text-teal-700 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            Our Process
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5" style={{ fontFamily: 'Poppins, sans-serif' }}>
            How Water Damage Restoration Works
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From the moment you call to the final walkthrough, Indoor-Restore Services follows a proven, step-by-step restoration process designed to minimize damage, protect your health, and get your Merced home or business back to normal as quickly as possible.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {steps.map((step, index) => (
            <div
              key={index}
              className="relative bg-gray-50 rounded-2xl p-8 border border-gray-100 hover:border-teal-200 hover:shadow-lg transition-all duration-300 group"
            >
              <div className="absolute top-6 right-6 text-5xl font-black text-gray-100 group-hover:text-teal-50 transition-colors select-none">
                {step.number}
              </div>
              <div className="w-14 h-14 bg-teal-600 rounded-xl flex items-center justify-center mb-5">
                <i className={`${step.icon} text-2xl text-white`}></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>

        {/* Editorial text block — boosts text-to-HTML ratio */}
        <div className="bg-teal-50 rounded-3xl p-10 md:p-14 border border-teal-100">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Why a Structured Restoration Process Matters
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                Water damage is deceptive. What appears to be a small leak on the surface can conceal extensive moisture saturation inside wall cavities, beneath flooring, and within structural framing. Without a systematic, equipment-driven approach, that hidden moisture becomes the breeding ground for toxic mold, wood rot, and long-term structural deterioration — problems that cost far more to fix later than to prevent now.
              </p>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                Indoor-Restore Services follows the <strong>IICRC S500 Standard for Professional Water Damage Restoration</strong> — the industry's most rigorous guideline for assessment, drying, and documentation. Every job we complete in Merced, Atwater, Los Banos, Turlock, and throughout the Central Valley is performed to this standard, ensuring your property is genuinely dry — not just surface-dry.
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                Our process also protects you legally and financially. Thorough documentation of all damage, drying logs, and moisture readings creates an airtight record for your insurance claim, reducing the risk of disputes and ensuring you receive the full payout your policy entitles you to.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                The Cost of Waiting — and the Value of Acting Fast
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                Every hour that passes after water damage occurs increases the total restoration cost and the risk of permanent damage. Within the first hour, water spreads to adjacent rooms and begins soaking into porous materials. Within 24 hours, drywall softens, wood swells, and mold spores begin to activate. By 48–72 hours, mold colonies are visible, structural materials may be compromised, and remediation costs can double or triple.
              </p>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                That is why Indoor-Restore Services maintains a 24/7 emergency response team specifically for Merced County and the surrounding Central Valley. Our rapid response — typically under 60 minutes — is not just a selling point. It is the single most important factor in limiting damage, reducing your out-of-pocket costs, and protecting your property's long-term value.
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                Whether you are dealing with a burst pipe, appliance overflow, sewage backup, storm flooding, or roof leak, the right action is the same: call a certified restoration professional immediately. Do not wait to see if it dries on its own — it won't.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
